"use client";

import React from 'react';
import Link from 'next/link';
import { ArrowLeft, Sparkles, Star, Heart, Leaf, Moon, Sun, Rainbow, Users } from 'lucide-react';
import { AboutLayout } from '@/components/about/about-layout';

export default function OraclePage() {
  return (
    <AboutLayout activeSection="tarot">
      <div className="max-w-4xl mx-auto px-4 py-8">
            <div className="mb-4 p-3 bg-teal-500/10 border border-teal-500/20 rounded-lg">
              <div className="flex items-center gap-2 text-teal-300">
                <Sparkles className="h-4 w-4" />
                <span className="text-sm font-medium">🃏 Tarot</span>
                <span className="text-teal-500">/</span>
                <span className="text-sm">Sistemler</span>
                <span className="text-teal-500">/</span>
                <span className="text-sm">Oracle Kartları</span>
              </div>
            </div>

            <div className="mb-6">
              <Link href="/about" className="inline-flex items-center text-slate-400 hover:text-slate-200 transition-colors">
                <ArrowLeft className="h-4 w-4 mr-2" />
                AXTROLOG Nedir
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <Link href="/about/tarot/systems" className="text-slate-400 hover:text-slate-200 transition-colors">
                Tarot Sistemleri
              </Link>
              <span className="text-slate-500 mx-2">/</span>
              <span className="text-slate-300">Oracle Kartları</span>
            </div>

            <div className="bg-gradient-to-r from-teal-900/20 to-cyan-900/20 rounded-xl p-8 mb-8 border border-teal-500/20">
              <h1 className="text-4xl font-bold text-slate-100 mb-4 flex items-center gap-3">
                <Sparkles className="h-10 w-10 text-teal-400" />
                Oracle Kartları
              </h1>
              <p className="text-lg text-slate-200 leading-relaxed">
                Serbest format kart sistemleri. Sabit yapı olmaksızın tema odaklı destelerin genel adı.
                Modern spiritual rehberlik ve kişisel gelişim amaçlı kullanılır.
              </p>
            </div>

            {/* Deck Structure Section */}
            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Deste Yapısı</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <Link
                  href="/about/tarot/systems/oracle/cards"
                  className="block group hover:bg-slate-750 transition-colors rounded-lg p-4 border border-purple-500/20 hover:border-purple-400/40"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-violet-700 rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform">
                      <Sparkles className="h-8 w-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold text-slate-100 group-hover:text-purple-300 transition-colors mb-2">
                        Oracle Kart Destesi
                      </h3>
                      <p className="text-slate-300 leading-relaxed mb-3">
                        Çeşitli temaları olan ve 20-80+ arasında değişen sayıda kart içeren oracle desteler
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                            <span className="text-sm text-slate-400">Esnek yapı (20-80+ kart)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-violet-400 rounded-full"></div>
                            <span className="text-sm text-slate-400">Tema odaklı desteler</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                            <span className="text-sm text-slate-400">İlham verici mesajlar</span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-violet-400 rounded-full"></div>
                            <span className="text-sm text-slate-400">Yeni başlayanlar için ideal</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                            <span className="text-sm text-slate-400">Çeşitli temalar (melekler, çakralar, tanrıçalar, hayvanlar)</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-violet-400 rounded-full"></div>
                            <span className="text-sm text-slate-400">Sabit kural sistemi yok</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-purple-400 group-hover:text-purple-300 transition-colors">
                      <ArrowLeft className="h-5 w-5 rotate-180" />
                    </div>
                  </div>
                </Link>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Oracle vs Tarot</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-700 rounded-lg flex items-center justify-center">
                      <Star className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">Tarot (Geleneksel)</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">78 kart sabit yapı</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Majör/Minör Arkana sistemi</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Centuries-old sembolik sistem</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-purple-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Karmaşık correspondence'lar</span>
                    </div>
                  </div>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-700 rounded-lg flex items-center justify-center">
                      <Sparkles className="h-6 w-6 text-white" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-100">Oracle (Modern)</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Değişken kart sayısı</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Tema odaklı yaklaşım</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Modern spiritual rehberlik</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 bg-teal-400 rounded-full"></div>
                      <span className="text-sm text-slate-400">Sezgisel, free-form kullanım</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Popüler Oracle Temaları</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[
                  { name: "Melekler", icon: "👼", color: "from-blue-500 to-indigo-700" },
                  { name: "Hayvan Ruhları", icon: "🦋", color: "from-green-500 to-emerald-700" },
                  { name: "Kristaller", icon: "💎", color: "from-purple-500 to-violet-700" },
                  { name: "Çiçekler", icon: "🌸", color: "from-pink-500 to-rose-700" },
                  { name: "Ay Fazları", icon: "🌙", color: "from-indigo-500 to-blue-700" },
                  { name: "Çakralar", icon: "🌈", color: "from-red-500 to-orange-700" },
                  { name: "Affirmasyonlar", icon: "✨", color: "from-yellow-500 to-amber-700" },
                  { name: "Tanrıçalar", icon: "👑", color: "from-violet-500 to-purple-700" },
                ].map((theme) => (
                  <div key={theme.name} className="bg-slate-800 rounded-lg p-4 border border-slate-700 text-center">
                    <div className={`w-12 h-12 bg-gradient-to-br ${theme.color} rounded-lg flex items-center justify-center mx-auto mb-3`}>
                      <span className="text-2xl">{theme.icon}</span>
                    </div>
                    <div className="text-slate-300 text-sm font-medium">{theme.name}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Oracle Kartları Özellikleri</h2>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-700 rounded-lg flex items-center justify-center">
                      <Heart className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Pozitif Mesajlar</h3>
                  </div>
                  <p className="text-slate-300 text-sm">
                    Çoğunlukla destekleyici, teşvik edici ve güçlendirici mesajlar.
                    Korkutucu veya negatif imagery'den kaçınılır.
                  </p>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-700 rounded-lg flex items-center justify-center">
                      <Leaf className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Basit Anlamlar</h3>
                  </div>
                  <p className="text-slate-300 text-sm">
                    Her karta kitapçıkta açık mesaj yazılır.
                    Karmaşık sembolik yoruma gerek yoktur.
                  </p>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-indigo-700 rounded-lg flex items-center justify-center">
                      <Rainbow className="h-5 w-5 text-white" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-100">Sanatsal Özgürlük</h3>
                  </div>
                  <p className="text-slate-300 text-sm">
                    Sanatçılar tamamen özgür yaratım yapabilir.
                    Geleneksel kurallara bağlı kalma zorunluluğu yok.
                  </p>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Kullanım Alanları</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-3">Kişisel Gelişim</h3>
                    <ul className="text-sm text-slate-400 space-y-2">
                      <li>• Günlük motivasyon ve ilham</li>
                      <li>• Self-care ve mindfulness</li>
                      <li>• Affirmation ve positive thinking</li>
                      <li>• Spiritual büyüme ve gelişim</li>
                      <li>• Meditation ve contemplation</li>
                    </ul>
                  </div>

                  <div>
                    <h3 className="text-lg font-semibold text-slate-100 mb-3">Terapötik Kullanım</h3>
                    <ul className="text-sm text-slate-400 space-y-2">
                      <li>• Conversation starter olarak</li>
                      <li>• Creative therapy destegi</li>
                      <li>• Journal writing prompts</li>
                      <li>• Group therapy activities</li>
                      <li>• Stress relief ve relaxation</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Ünlü Oracle Deste Örnekleri</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-3">Klasik Oracles</h3>
                  <ul className="text-sm text-slate-400 space-y-2">
                    <li>• <strong>Angel Oracle Cards:</strong> Doreen Virtue</li>
                    <li>• <strong>Goddess Guidance Oracle Cards:</strong> Doreen Virtue</li>
                    <li>• <strong>Power Animal Oracle Cards:</strong> Steven Farmer</li>
                    <li>• <strong>Earth Magic Oracle Cards:</strong> Steven Farmer</li>
                  </ul>
                </div>

                <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                  <h3 className="text-lg font-semibold text-slate-100 mb-3">Modern Oracles</h3>
                  <ul className="text-sm text-slate-400 space-y-2">
                    <li>• <strong>Work Your Light Oracle:</strong> Rebecca Campbell</li>
                    <li>• <strong>The Universe Has Your Back:</strong> Gabrielle Bernstein</li>
                    <li>• <strong>Sacred Rebels Oracle:</strong> Alana Fairchild</li>
                    <li>• <strong>Wild Offering Oracle:</strong> Tosha Silver</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="mb-8">
              <h2 className="text-2xl font-semibold text-slate-100 mb-6">Basit Kullanım Teknikleri</h2>
              <div className="bg-slate-800 rounded-xl p-6 border border-slate-700">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="text-center p-4 bg-slate-700 rounded-lg">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-700 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <Sun className="h-6 w-6 text-white" />
                    </div>
                    <h4 className="font-semibold text-slate-200 mb-2">Günlük Çekim</h4>
                    <p className="text-sm text-slate-400">Her sabah bir kart çekerek güne odakla başla.</p>
                  </div>

                  <div className="text-center p-4 bg-slate-700 rounded-lg">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-violet-700 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <Moon className="h-6 w-6 text-white" />
                    </div>
                    <h4 className="font-semibold text-slate-200 mb-2">Soru Sorma</h4>
                    <p className="text-sm text-slate-400">Belirli bir konuda rehberlik için kart çek.</p>
                  </div>

                  <div className="text-center p-4 bg-slate-700 rounded-lg">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-700 rounded-lg flex items-center justify-center mx-auto mb-3">
                      <Heart className="h-6 w-6 text-white" />
                    </div>
                    <h4 className="font-semibold text-slate-200 mb-2">Meditation</h4>
                    <p className="text-sm text-slate-400">Kartın mesajı üzerinde düşünme ve içsel keşif.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-amber-900/20 border border-amber-700/50 rounded-xl p-6 mb-8">
              <h3 className="text-lg font-semibold text-amber-200 mb-3">⚠️ Önemli Uyarı</h3>
              <p className="text-amber-100 leading-relaxed">
                Oracle kartları sembolik rehberlik ve inspiration aracıdır ve kesinlik iddiası taşımaz. İçerikler tamamen
                merak, eğlence ve kişisel farkındalık amaçlıdır. Yaşamsal kararlar için uzman görüşü alınmalıdır.
              </p>
            </div>

            {/* Main Tarot Training CTA */}
            <div className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-xl p-6 mb-8">
              <div className="text-center">
                <div className="flex justify-center mb-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                    <Users className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="text-2xl font-semibold text-slate-100 mb-3">🎴 Tarot Eğitimleri</h3>
                <p className="text-slate-300 mb-6 leading-relaxed max-w-2xl mx-auto">
                  Tarot kart sistemleri, sembolik yorumlama teknikleri, kart dizilimleri ve okuma yöntemleri hakkında
                  detaylı eğitim programları için kayıt olun. Merak ettiğiniz konularda rehberlik alın.
                </p>
                <button className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold rounded-lg transition-all transform hover:scale-105 shadow-lg">
                  <Users className="h-5 w-5 mr-2" />
                  Tarot Eğitimi İçin Kayıt Ol
                </button>
                <p className="text-slate-400 text-sm mt-3">
                  * Eğitim programları sadece bilgilendirme ve kişisel gelişim amaçlıdır
                </p>
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between items-center">
              <Link
                href="/about/tarot/systems/lenormand"
                className="inline-flex items-center px-4 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-600 rounded-lg text-slate-300 hover:text-slate-100 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Lenormand Kartları
              </Link>

              <Link
                href="/about/tarot/systems/modern"
                className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                Modern Tarot Sistemleri
                <ArrowLeft className="h-4 w-4 ml-2 rotate-180" />
              </Link>
            </div>
      </div>
    </AboutLayout>
  );
}